import { Box, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import useRolePermissions from '../../../../hooks/useRolePermissions/useRolePermissions';
import usePortalConfig from '../../../../hooks/usePortalConfig/usePortalConfig';

import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import { RootState } from '../../../../stores/Store';

import { UserOperationsType } from '../../../../models/UsersModels';

import MenuItemWrapper from '../../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../../components/Menu/MenuStyle';

interface UserDetailsOptionsProps {
  formOpen: boolean;
  editUser: () => void;
  assignDevice: () => void;
  addDevice: () => void;
  assignSim?: () => void;
  deleteUser: () => void;
  isPerformingAction: boolean;
}
function UserDetailsOptions({
  formOpen,
  editUser,
  assignDevice,
  addDevice,
  assignSim,
  deleteUser,
  isPerformingAction,
}: UserDetailsOptionsProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const user = useContext(UserDetailsContext);
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();
  const { userOperations } = useSelector((state: RootState) => state.eSimManagerReducer);

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Render edit info option
   */
  const renderEditInfoOption = () => {
    if (isUserAllowed(routePermissions.editUsers)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            editUser();
          }}
          disabled={
            user === null ||
            !userOperations.includes(UserOperationsType.CAN_EDIT) ||
            sessionStorage.getItem('impersonateId') !== null
          }
        >
          {t('pages.userDetails.editInformation')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render add device option
   */
  const renderAddDeviceOption = () => {
    if (isUserAllowed(routePermissions.devicesCreate)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            addDevice();
          }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.userDetails.addDevice')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render assign device option
   */
  const renderAssignDeviceOption = () => {
    if (userOperations.includes(UserOperationsType.CAN_ASSIGN_DEVICE_TO_USER)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            assignDevice();
          }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.userDetails.assignDevice')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render assign sim option
   */
  const renderAssignSimOption = () => {
    if (userOperations.includes(UserOperationsType.CAN_ASSIGN_SIM_TO_USER) && assignSim) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            assignSim();
          }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.assignSim.title')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render delete option
   */
  const renderDeleteOption = () => {
    if (userOperations.includes(UserOperationsType.CAN_DELETE)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            deleteUser();
          }}
          sx={{ color: theme.palette.primary.main }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.userDetails.deleteUser')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Can render options
   */
  const renderOptions = () =>
    !formOpen &&
    (isUserAllowed(routePermissions.editUsers) ||
      isUserAllowed(routePermissions.devicesCreate) ||
      userOperations.includes(UserOperationsType.CAN_ASSIGN_DEVICE_TO_USER) ||
      userOperations.includes(UserOperationsType.CAN_ASSIGN_SIM_TO_USER) ||
      userOperations.includes(UserOperationsType.CAN_DELETE));

  return (
    <>
      {renderOptions() ? (
        <Box
          id={`box-${user?.id}`}
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0.375rem',
            width: '2.25rem',
            height: '2.25rem',
            flexShrink: 0,
            ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
          }}
          onClick={handleClick}
        >
          <VerticalDotsOutlineIcon />
        </Box>
      ) : null}
      <Box>
        <MenuWrapper
          id={`menu-${user?.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiPaper-root': {
              minWidth: '11.875rem',
            },
          }}
        >
          {renderEditInfoOption()}
          {renderAddDeviceOption()}
          {renderAssignDeviceOption()}
          {renderAssignSimOption()}
          {renderDeleteOption()}
        </MenuWrapper>
      </Box>
    </>
  );
}

export default UserDetailsOptions;
